import { AnimatePresence, motion } from 'framer-motion'

import { AuthPayload } from '@/components/auth/AuthPayload.jsx'
import grainUrl from '@/assets/auth-grain.svg'

// The dark "machine" art panel that sits on the right of every auth /
// onboarding screen. Near-black with warm-red atmospheric bloom, dry grain
// and faint scan-lines, mirroring the website blog-post hero recipe. Content
// changes per page / step via the `slide` prop; `payload` renders the animated
// JSON payload on top.

// Glow presets: each moves the warm-red radial blooms so consecutive steps
// feel distinct without new assets.
const GLOWS = {
    a: `radial-gradient(58% 78% at 18% 32%, rgba(217,58,58,.48), transparent 62%),
        radial-gradient(52% 70% at 82% 22%, rgba(240,127,127,.30), transparent 60%),
        radial-gradient(60% 90% at 60% 120%, rgba(120,60,60,.35), transparent 60%)`,
    b: `radial-gradient(56% 74% at 78% 30%, rgba(217,58,58,.44), transparent 62%),
        radial-gradient(50% 66% at 20% 20%, rgba(240,127,127,.26), transparent 60%),
        radial-gradient(64% 92% at 40% 118%, rgba(120,60,60,.34), transparent 60%)`,
    c: `radial-gradient(62% 80% at 50% 18%, rgba(217,58,58,.42), transparent 60%),
        radial-gradient(48% 64% at 14% 70%, rgba(240,127,127,.24), transparent 58%),
        radial-gradient(58% 88% at 82% 108%, rgba(120,60,60,.32), transparent 60%)`,
}

const SHEEN = `linear-gradient(118deg, rgba(255,255,255,.18) 0%, rgba(255,255,255,0) 18%),
    linear-gradient(152deg, rgba(255,255,255,0) 38%, rgba(255,255,255,.13) 50%, rgba(255,255,255,0) 63%),
    repeating-linear-gradient(90deg, rgba(255,255,255,.07) 0 1px, transparent 1px 72px)`

export function AuthArtPanel({ slide, className, payload }) {

    const glow = GLOWS[slide?.glow] || GLOWS.a

    return (
        <div
            className={`relative h-full w-full overflow-hidden bg-auth-machine text-auth-machine-ink ${className || ''}`}
        >
            <div className="absolute inset-0" style={{ backgroundImage: glow }} />
            <div
                className="absolute inset-0"
                style={{ backgroundImage: `url(${grainUrl})`, backgroundSize: '9%', opacity: 0.13, mixBlendMode: 'overlay' }}
            />
            <div
                className="absolute inset-0"
                style={{ backgroundImage: SHEEN, opacity: 0.55, mixBlendMode: 'screen' }}
            />

            <div className="relative z-10 flex h-full flex-col justify-end p-12 lg:p-16">
                {payload && (
                    <div className="mb-auto pt-4">
                        <AuthPayload variant={payload} />
                    </div>
                )}
                <AnimatePresence mode="wait">
                    <motion.div
                        key={slide?.id || 'default'}
                        initial={{ opacity: 0, y: 8 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -8 }}
                        transition={{ duration: 0.2, ease: 'easeOut' }}
                        className="max-w-md"
                    >
                        {slide?.motif && (
                            <div className="mb-8">
                                {slide.motif}
                            </div>
                        )}
                        {slide?.eyebrow && (
                            <div className="mb-4 font-mono text-[11px] font-medium uppercase tracking-[0.14em] text-auth-accent-soft">
                                {slide.eyebrow}
                            </div>
                        )}
                        {slide?.title && (
                            <h2 className="text-3xl font-light leading-[1.1] tracking-[-0.02em] lg:text-4xl">
                                {slide.title}
                            </h2>
                        )}
                        {slide?.subtitle && (
                            <p className="mt-4 max-w-sm text-sm leading-relaxed text-auth-machine-mute">
                                {slide.subtitle}
                            </p>
                        )}
                    </motion.div>
                </AnimatePresence>
            </div>
        </div>
    )
}
